import React from 'react'
import { debounce } from 'lodash'
import { useFirstRender } from 'modules/react/hooks/useFirstRender'

const GlobalSearchContext = React.createContext({})

export default function GlobalSearchContainer ({
  children,
  restUrl,
  defaultSearchValue,
  errorImage
}) {

  const [loading, setLoading] = React.useState(false)
  const [search, setSearch] = React.useState(defaultSearchValue ?? '')
  const [results, setResults] = React.useState([])
  const [activePage, setActivePage] = React.useState(1)
  const [totalPages, setTotalPages] = React.useState(1)
  const [totalPosts, setTotalPosts] = React.useState(0)
  const [containerRef, setContainerRef] = React.useState(null)

  const firstRender = useFirstRender()

  const setDebouncedSearch = React.useCallback(debounce((e) => {
    setActivePage(1)
    setSearch(e.target.value)
  }, 500), [])

  const updateHistory = () => {
    const url = new URL(window.location.href.split('?')[0])
    url.search = new URLSearchParams(`s=${search}`)
    window.history.pushState('', '', decodeURIComponent(url.toString()))
  }

  const doSearch = () => {
    updateHistory()

    if (search === '') {
      setResults([])
      setLoading(false)
      setActivePage(1)
      setTotalPages(1)
      setTotalPosts(0)
      return
    }

    fetch(restUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        's': search,
        'paged': activePage
      })
    })
      .then(response => response.json())
      .then(response => {
        setLoading(false)
        setResults(response.results)
        setTotalPages(response.totalPages)
        setTotalPosts(response.totalPosts)
      })
  }

  const scrollToTop = () => {
    if (containerRef?.current) {
      const headerOffset = 120
      const elementPosition = containerRef.current.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  React.useEffect(() => {
    doSearch()
  }, [search])

  React.useEffect(() => {
    if (firstRender) {
      return
    }

    doSearch()
    scrollToTop()
  }, [activePage])

  return (
    <GlobalSearchContext.Provider value={{
      defaultSearchValue,
      search,
      setDebouncedSearch,
      results,
      activePage,
      setActivePage,
      totalPages,
      totalPosts,
      setContainerRef,
      loading,
      setLoading,
      errorImage
    }}>
      {children}
    </GlobalSearchContext.Provider>
  )
}

export { GlobalSearchContext }